import React, { useEffect, useState } from 'react';
import { RefreshCw, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Trash2, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-EC', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const ESTADO_COLORS = {
  nuevo: { bg: '#E3F2FD', color: '#1565C0' },
  contactado: { bg: '#FFF3E0', color: '#E65100' },
  convertido: { bg: '#E8F5E9', color: '#2E7D32' },
  descartado: { bg: '#FAFAFA', color: '#9E9E9E' },
};

const ORIGEN_LABEL = { miami: 'Miami (USA)', china: 'China (Shenzhen)' };

const FUENTE_LABEL = { cotizador: 'Cotizador', contacto: 'Contacto', importador: 'Importador' };
const FUENTE_COLORS = {
  cotizador: { bg: '#F3E5F5', color: '#6A1B9A' },
  contacto: { bg: '#E0F2F1', color: '#00695C' },
  importador: { bg: '#FFF4CC', color: '#7A5A00' },
};

const vacio = (v) => v === null || v === undefined;
const dinero = (v, decimales) => (vacio(v) ? '—' : `$${Number(v).toFixed(decimales)}`);

// De dónde vino el lead, para medir campañas (UTMs / clic de Google Ads).
function campanaDe(tracking) {
  const t = tracking || {};
  if (t.utm_campaign) return `${t.utm_source || 'campaña'} · ${t.utm_campaign}`;
  if (t.utm_source) return t.utm_source;
  if (t.gclid) return 'Google Ads';
  return 'Directo / orgánico';
}

export default function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroOrigen, setFiltroOrigen] = useState('');
  const [filtroFuente, setFiltroFuente] = useState('');
  const [pagina, setPagina] = useState(1);
  const [porPagina] = useState(20);
  const [editandoNotas, setEditandoNotas] = useState(null);
  const [notasTemp, setNotasTemp] = useState('');
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [expandido, setExpandido] = useState(null);
  const [equipo, setEquipo] = useState([]); // admins y asistentes, para asignar leads

  const cargar = () => {
    setCargando(true);
    setSeleccionados(new Set());
    const params = { ordering: '-creado' };
    if (filtro) params.search = filtro;
    if (filtroEstado) params.estado = filtroEstado;
    if (filtroOrigen) params.origen = filtroOrigen;
    if (filtroFuente) params.fuente = filtroFuente;
    api.get('/leads/', { params })
      .then((res) => setLeads(res.data.results || res.data))
      .catch(() => toast.error('No se pudieron cargar los leads.'))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    api.get('/usuarios/')
      .then((res) => setEquipo((res.data.results || res.data).filter((u) => u.is_active && (u.rol === 'admin' || u.rol === 'asistente'))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setFiltro(busqueda.trim()), 350);
    return () => clearTimeout(timer);
  }, [busqueda]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [filtro, filtroEstado, filtroOrigen, filtroFuente]);
  useEffect(() => setPagina(1), [filtro, filtroEstado, filtroOrigen, filtroFuente]);

  const totalPaginas = Math.max(1, Math.ceil(leads.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const leadsPaginados = leads.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

  const cambiarEstado = async (id, nuevoEstado) => {
    try {
      await api.patch(`/leads/${id}/`, { estado: nuevoEstado });
      setLeads((prev) => prev.map((l) => l.id === id ? { ...l, estado: nuevoEstado } : l));
      toast.success('Estado actualizado.');
    } catch {
      toast.error('No se pudo actualizar.');
    }
  };

  // Guarda un dato de calificación (calidad, valor estimado, asignado a) y refresca esa fila.
  const guardarCalificacion = async (id, cambios) => {
    try {
      const res = await api.patch(`/leads/${id}/`, cambios);
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...res.data } : l)));
      toast.success('Guardado.');
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudo guardar.'));
    }
  };

  const guardarNotas = async (id) => {
    try {
      await api.patch(`/leads/${id}/`, { notas: notasTemp });
      setLeads((prev) => prev.map((l) => l.id === id ? { ...l, notas: notasTemp } : l));
      setEditandoNotas(null);
      toast.success('Notas guardadas.');
    } catch {
      toast.error('No se pudo guardar.');
    }
  };

  const toggleSeleccion = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTodosPagina = () => {
    const idsPagina = leadsPaginados.map((l) => l.id);
    const todosSeleccionados = idsPagina.every((id) => seleccionados.has(id));
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (todosSeleccionados) {
        idsPagina.forEach((id) => next.delete(id));
      } else {
        idsPagina.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const eliminarSeleccionados = async () => {
    const ids = Array.from(seleccionados);
    if (!window.confirm(`¿Eliminar ${ids.length} lead(s) seleccionado(s)?`)) return;
    try {
      await api.post('/leads/eliminar_lote/', { ids });
      toast.success(`${ids.length} lead(s) eliminado(s).`);
      setSeleccionados(new Set());
      cargar();
    } catch {
      toast.error('No se pudieron eliminar.');
    }
  };

  const resumen = {
    total: leads.length,
    nuevos: leads.filter((l) => l.estado === 'nuevo').length,
    contactados: leads.filter((l) => l.estado === 'contactado').length,
    convertidos: leads.filter((l) => l.estado === 'convertido').length,
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Total leads</div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>{resumen.total}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Nuevos</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1565C0' }}>{resumen.nuevos}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Contactados</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#E65100' }}>{resumen.contactados}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Convertidos</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#2E7D32' }}>{resumen.convertidos}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>Leads</h2>
        <div className="toolbar">
          {seleccionados.size > 0 && (
            <button className="btn-ghost btn-sm" onClick={eliminarSeleccionados} style={{ color: '#D32F2F', whiteSpace: 'nowrap' }}>
              <Trash2 size={14} /> Eliminar ({seleccionados.size})
            </button>
          )}
          <div className="toolbar__buscar">
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
            <input
              type="text" placeholder="Buscar nombre, email, teléfono..."
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              style={{ minWidth: '180px', paddingLeft: '32px' }}
            />
          </div>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={{ minWidth: '140px' }}>
            <option value="">Todos los estados</option>
            <option value="nuevo">Nuevo</option>
            <option value="contactado">Contactado</option>
            <option value="convertido">Convertido</option>
            <option value="descartado">Descartado</option>
          </select>
          <select value={filtroFuente} onChange={(e) => setFiltroFuente(e.target.value)} style={{ minWidth: '140px' }}>
            <option value="">Todas las fuentes</option>
            <option value="cotizador">Cotizador</option>
            <option value="contacto">Formulario de contacto</option>
            <option value="importador">Página de importadores</option>
          </select>
          <select value={filtroOrigen} onChange={(e) => setFiltroOrigen(e.target.value)} style={{ minWidth: '140px' }}>
            <option value="">Todos los orígenes</option>
            <option value="miami">Miami</option>
            <option value="china">China</option>
          </select>
          <button className="btn-secondary" onClick={cargar} style={{ whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ width: '36px' }}>
                <input
                  type="checkbox"
                  checked={leadsPaginados.length > 0 && leadsPaginados.every((l) => seleccionados.has(l.id))}
                  onChange={toggleTodosPagina}
                />
              </th>
              <th>Fecha</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Interés</th>
              <th>Origen</th>
              <th>Valor</th>
              <th>Peso</th>
              <th>Total Est.</th>
              <th>Estado</th>
              <th>Notas</th>
              <th style={{ width: '36px' }} />
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={12} className="text-secondary">Cargando...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={12} className="text-secondary">No hay leads registrados.</td></tr>
            ) : leadsPaginados.map((lead) => {
              const ec = ESTADO_COLORS[lead.estado] || ESTADO_COLORS.nuevo;
              const fc = FUENTE_COLORS[lead.fuente] || FUENTE_COLORS.cotizador;
              const abierto = expandido === lead.id;
              const wa = lead.telefono ? `https://wa.me/${lead.telefono.replace(/\D/g, '')}` : null;
              return (
                <React.Fragment key={lead.id}>
                  <tr style={seleccionados.has(lead.id) ? { background: 'var(--color-bg-secondary)' } : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        checked={seleccionados.has(lead.id)}
                        onChange={() => toggleSeleccion(lead.id)}
                      />
                    </td>
                    <td className="text-sm text-secondary" style={{ whiteSpace: 'nowrap' }}>{formatFecha(lead.creado)}</td>
                    <td style={{ fontWeight: 600 }}>{lead.nombre}</td>
                    <td>
                      <a href={`mailto:${lead.email}`} style={{ color: 'var(--color-primary)' }}>
                        {lead.email}
                      </a>
                    </td>
                    <td className="text-sm">
                      <span style={{ background: fc.bg, color: fc.color, borderRadius: '6px', padding: '2px 6px', fontWeight: 600, fontSize: '11px', marginRight: '6px' }}>
                        {FUENTE_LABEL[lead.fuente] || lead.fuente}
                      </span>
                      {lead.fuente === 'contacto' ? lead.tipo_servicio_display : lead.tipo_articulo}
                    </td>
                    <td className="text-sm">{ORIGEN_LABEL[lead.origen] || lead.origen || '—'}</td>
                    <td className="text-sm mono">{dinero(lead.valor_compra, 0)}</td>
                    <td className="text-sm mono">{vacio(lead.peso_lb) ? '—' : `${Number(lead.peso_lb).toFixed(1)} lb`}</td>
                    <td className="mono" style={{ fontWeight: 600 }}>{dinero(lead.total_estimado, 2)}</td>
                    <td>
                      <select
                        value={lead.estado}
                        onChange={(e) => cambiarEstado(lead.id, e.target.value)}
                        style={{
                          background: ec.bg, color: ec.color,
                          border: 'none', borderRadius: '6px',
                          padding: '4px 8px', fontWeight: 600,
                          fontSize: '12px', cursor: 'pointer',
                          minWidth: '110px',
                        }}
                      >
                        <option value="nuevo">Nuevo</option>
                        <option value="contactado">Contactado</option>
                        <option value="convertido">Convertido</option>
                        <option value="descartado">Descartado</option>
                      </select>
                    </td>
                    <td>
                      {editandoNotas === lead.id ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <input
                            type="text" value={notasTemp}
                            onChange={(e) => setNotasTemp(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && guardarNotas(lead.id)}
                            style={{ fontSize: '12px', padding: '4px 6px', minWidth: '120px' }}
                            autoFocus
                          />
                          <button className="btn-ghost btn-sm" onClick={() => guardarNotas(lead.id)}>OK</button>
                          <button className="btn-ghost btn-sm" onClick={() => setEditandoNotas(null)}>X</button>
                        </div>
                      ) : (
                        <span
                          className="text-sm text-secondary"
                          style={{ cursor: 'pointer', minWidth: '60px', display: 'inline-block' }}
                          onClick={() => { setEditandoNotas(lead.id); setNotasTemp(lead.notas || ''); }}
                          title="Click para agregar notas"
                        >
                          {lead.notas || '+ nota'}
                        </span>
                      )}
                    </td>
                    <td>
                      <button className="btn-ghost btn-sm" onClick={() => setExpandido(abierto ? null : lead.id)} aria-label="Ver detalle">
                        {abierto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </td>
                  </tr>
                  {abierto && (
                    <tr>
                      <td colSpan={12} style={{ padding: 'var(--spacing-4)', background: 'var(--color-bg-secondary)' }}>
                        {lead.fuente !== 'cotizador' ? (
                          <div className="text-sm" style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                            <div>
                              <strong>Teléfono / WhatsApp:</strong>{' '}
                              {lead.telefono ? (
                                <a href={wa} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                                  <MessageCircle size={12} style={{ verticalAlign: '-1px' }} /> {lead.telefono}
                                </a>
                              ) : 'No indicó'}
                              {' · '}<strong>Ciudad:</strong> {lead.ciudad || '—'}
                            </div>
                            <div><strong>Mensaje:</strong></div>
                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{lead.mensaje || '(sin mensaje)'}</div>
                            {lead.fuente === 'importador' && lead.detalle && lead.detalle.resultado && (
                              <div style={{ background: 'var(--color-bg)', borderRadius: '8px', padding: 'var(--spacing-2) var(--spacing-3)' }}>
                                <strong>Cálculo que vio el cliente (preliminar):</strong>{' '}
                                {lead.detalle.resultado.manual
                                  ? `Requiere cotización manual. ${(lead.detalle.resultado.motivos_manual || []).join(' ')}`
                                  : `Ecuafly ${dinero(lead.detalle.resultado.totales.pagar_ecuafly, 2)} · Impuestos de Aduana ${dinero(lead.detalle.resultado.totales.impuestos_aduana, 2)} · Costo de importar ${dinero(lead.detalle.resultado.totales.costo_importar, 2)}`}
                                {(lead.detalle.resultado.alertas || []).map((a, i) => (
                                  <div key={i} className="text-xs text-secondary">• {a}</div>
                                ))}
                              </div>
                            )}
                            <div className="text-xs text-secondary">
                              <strong>Campaña:</strong> {campanaDe(lead.tracking)}
                              {lead.tracking && lead.tracking.landing_page && <> · <strong>Página:</strong> {lead.tracking.landing_page}</>}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm">
                            <strong>Servicios extra en la cotización:</strong>{' '}
                            {[lead.consolidacion && 'Consolidación', lead.reempaque && 'Reempaque', lead.seguro && 'Seguro'].filter(Boolean).join(', ') || 'Ninguno'}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', marginTop: 'var(--spacing-3)', alignItems: 'flex-end' }}>
                          <div>
                            <label className="text-xs">Calidad del lead</label>
                            <select
                              value={lead.calidad || ''}
                              onChange={(e) => guardarCalificacion(lead.id, { calidad: e.target.value || null })}
                              style={{ width: 'auto', minWidth: '130px' }}
                            >
                              <option value="">Sin calificar</option>
                              <option value="alta">Alta</option>
                              <option value="media">Media</option>
                              <option value="baja">Baja</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs">Valor estimado (USD)</label>
                            <input
                              type="number" min="0" step="0.01" placeholder="0.00"
                              defaultValue={lead.valor_estimado ?? ''}
                              key={`ve-${lead.id}-${lead.valor_estimado}`}
                              onBlur={(e) => {
                                const nuevo = e.target.value === '' ? null : e.target.value;
                                if (String(nuevo ?? '') !== String(lead.valor_estimado ?? '')) guardarCalificacion(lead.id, { valor_estimado: nuevo });
                              }}
                              style={{ width: '140px' }}
                            />
                          </div>
                          <div>
                            <label className="text-xs">Asignado a</label>
                            <select
                              value={lead.asignado_a || ''}
                              onChange={(e) => guardarCalificacion(lead.id, { asignado_a: e.target.value || null })}
                              style={{ width: 'auto', minWidth: '180px' }}
                            >
                              <option value="">Sin asignar</option>
                              {equipo.map((u) => (
                                <option key={u.id} value={u.id}>{`${u.first_name} ${u.last_name}`.trim() || u.username}</option>
                              ))}
                            </select>
                          </div>
                          {lead.canal && <div className="text-xs text-secondary"><strong>Canal:</strong> {lead.canal}</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {leads.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 'var(--spacing-4)', gap: 'var(--spacing-2)' }}>
          <span className="text-sm text-secondary">
            {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, leads.length)} de {leads.length}
          </span>
          <button className="btn-ghost btn-sm" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}>
            <ChevronLeft size={16} />
          </button>
          <button className="btn-ghost btn-sm" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
